import * as React from "react";
import { DeleteNote, UpdateNote } from "./assessment-result/Note";
import AssessmentResultObservations, {
  GetSeverityAssessmentRulesAction,
} from "./assessment-result/AssessmentResultObservations";
import AssessmentResultHeader from "./assessment-result/AssessmentResultHeader";
import AssessmentResultTreatmentOptions from "./assessment-result/AssessmentResultTreatmentOptions";
import { DownloadGalleryPhotosAction } from "./commons/components/AlbumViewer";
import ModalAlbumViewer from "./commons/components/modal/ModalAlbumViewer";
import AnalysedCriterion from "./commons/model/treatment/AnalysedCriterion";
import Photo from "./commons/model/Photo";
import Report, { assembleReport, ReportModel } from "./commons/model/report/Report";
import TreatmentSelection from "./commons/model/treatment/TreatmentSelection";
import SatisfactionForm from "./satisfaction/SatisfactionForm";
import { BookingRequestForm } from "./booking/BookingRequestForm";
import sfetch from "./commons/utils/super-fetch";
import AssessmentResultSmileSimulation from "./assessment-result/AssessmentResultSmileSimulation";
import Scan, {
  LandscapeDescriptors,
  MouthDescriptor,
  PortraitDescriptors,
  SmilePredictionDescriptors,
  SmileWithApplianceDescriptors,
} from "./commons/model/Scan";
import Country from "./commons/model/address/Country";
import TextAction from "./commons/components/action/TextAction";
import { EditSharingAction, EditSharingOption, ShareReportWithPatientAction } from "./reports/ReportModels";
import ShareReportModal from "./reports/ShareReportModal";
import { createRoot } from "react-dom/client";
import { ShareMailIcon } from "./commons/components/images/ShareMailIcon";

interface AssessmentResultProps {
  model: ReportModel;
  actions: Actions;
  demoMode: boolean;
  cssUrl: string;
  countries: Country[];
  availableTabs: AssessmentResultTab[];
  bookingExtendedModule: boolean;
  isAuthenticated: boolean;
  patientPortalAccountCreationUrl?: string;
}

interface Actions {
  downloadGalleryPhotos?: DownloadGalleryPhotosAction;
  bookingAppointment?: BookingAppointmentAction;
  shareWithPatient?: ShareReportWithPatientAction;
  editSharing?: EditSharingAction;
  updateNote?: UpdateNote;
  deleteNote?: DeleteNote;
  getSeverityAssessmentRules?: GetSeverityAssessmentRulesAction;
  troubleshoot?: TroubleshootAction;
  updateIssuesToTreat?: UpdateIssuesToTreatAction;
  goToPatientFile?: GoToPatientFile;
  allowPrint: boolean;
}

interface BookingAppointmentAction {
  bookingApiUrl: string;
  doctorBookingSystemUrl?: string;
}

interface AssessmentResultState {
  report: Report;
  showAlbum: boolean;
  selectedPhoto?: Photo;
  showShareReport: boolean;
  activeTab: AssessmentResultTab;
  selectedTreatmentSelection?: TreatmentSelection;
  editSharing?: EditSharingOption;
}

enum AssessmentResultTab {
  OBSERVATIONS = "OBSERVATIONS",
  TREATMENT_OPTIONS = "TREATMENT_OPTIONS",
  SMILE_SIMULATION = "SMILE_SIMULATION",
}

export interface TroubleshootAction {
  url: string;
}

export interface UpdateIssuesToTreatAction {
  url: string;
}

export interface GoToPatientFile {
  url: string;
}

export const LOCAL_STORAGE_PRINTING_MODEL_KEY: string = "printableReportModel";

export default class AssessmentResult extends React.Component<
  AssessmentResultProps,
  AssessmentResultState
> {
  // sjahan 28/08/19: This is the authoritative source of data.
  // If you need to update the state, work on this object, then update the state using assembleReport(this.model).
  private readonly model: ReportModel;
  private useHash: boolean = true;
  private readonly displayedTabs: AssessmentResultTab[];
  private readonly tabLabels = {
    OBSERVATIONS: i18n["Page.AssessmentResult.Tab.Observations"],
    TREATMENT_OPTIONS: i18n["Page.AssessmentResult.Tab.TreatmentOptions"],
    SMILE_SIMULATION: i18n["Page.AssessmentResult.Tab.SmileSimulation"],
  };

  constructor(props: AssessmentResultProps) {
    super(props);
    this.model = { ...props.model };

    this.displayedTabs = this.computeDisplayedTabs();
    this.state = {
      showAlbum: false,
      showShareReport: false,
      activeTab: this.displayedTabs[0],
      report: assembleReport(this.model),
      editSharing: props.actions.editSharing,
    };

    this.saveTreatmentSelection = this.saveTreatmentSelection.bind(this);
    this.toggleTreatedIssue = this.toggleTreatedIssue.bind(this);
    this.updateModelWithNote = this.updateModelWithNote.bind(this);
  }

  private computeDisplayedTabs() {
    return this.props.availableTabs.filter((tab) => {
      switch (tab) {
        case AssessmentResultTab.OBSERVATIONS:
          return true;
        case AssessmentResultTab.SMILE_SIMULATION:
          return this.props.model.smileSimulation.enabled;
        case AssessmentResultTab.TREATMENT_OPTIONS:
          return this.props.model.treatmentSelections.length;
      }
    });
  }

  private renderTab(tab: AssessmentResultTab) {
    switch (tab) {
      case AssessmentResultTab.OBSERVATIONS:
        return (
          <AssessmentResultObservations
            report={this.state.report}
            onSelectPhoto={this.showAlbumWithPhoto}
            getSeverityAssessmentRulesAction={this.props.actions.getSeverityAssessmentRules}
            goToTreatmentSelection={(criterion) =>
              this.setState({
                selectedTreatmentSelection: this.state.report.treatmentSelections.find((it) =>
                  it.criteria.includes(criterion),
                ),
                activeTab: AssessmentResultTab.TREATMENT_OPTIONS,
              })
            }
            criterionUpdatable={this.state.report.canEdit}
            useHash={this.useHash}
            isAuthenticated={this.props.isAuthenticated}
          />
        );

      case AssessmentResultTab.TREATMENT_OPTIONS:
        return (
          <AssessmentResultTreatmentOptions
            report={this.state.report}
            updateNote={this.props.actions.updateNote}
            deleteNote={this.props.actions.deleteNote}
            onTreatmentOptionChange={this.saveTreatmentSelection}
            onTreatedIssueChange={this.toggleTreatedIssue}
            onNoteChange={this.updateModelWithNote}
            demoMode={this.props.demoMode}
            selectedTreatmentSelection={this.state.selectedTreatmentSelection}
          />
        );

      case AssessmentResultTab.SMILE_SIMULATION:
        const scans = this.props.model.scanPackages.face;
        return (
          <AssessmentResultSmileSimulation
            smileWithApplianceScans={this.findScansInOrder(scans, SmileWithApplianceDescriptors)}
            smilePredictionScans={this.findScansInOrder(scans, SmilePredictionDescriptors)}
            initialSmileWithAppliancePicture={this.findScan(
              scans,
              MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED_CORRECTED,
              MouthDescriptor.FACE_MOUTH_SLIGHTLY_OPENED,
            )}
            initialSmilePredictionPicture={this.findScan(
              scans,
              MouthDescriptor.FACE_SMILING_CORRECTED,
              MouthDescriptor.FACE_SMILING,
            )}
            smileWithApplianceAvailable={
              this.props.model.smileSimulation.smileWithApplianceAvailable
            }
            smileWithApplianceErrorDescription={
              this.props.model.smileSimulation.smileWithApplianceErrorDescription
            }
            smilePredictionAvailable={this.props.model.smileSimulation.smilePredictionAvailable}
            smilePredictionErrorDescription={
              this.props.model.smileSimulation.smilePredictionErrorDescription
            }
            isPatient={this.props.model.patientOfReport}
            settings={this.props.model.smileSimulation.settings}
            flowUrl={this.props.model.flowUrl}
            canRetry={this.props.model.flowUrl !== null && this.props.model.canRetry}
          />
        );
    }
  }

  private findScansInOrder(scans: Scan[], descriptors: MouthDescriptor[]) {
    return scans
      .filter((scan) => descriptors.includes(scan.descriptor))
      .sort(Scan.orderByDescriptor(descriptors));
  }

  private findScan(
    scans: Scan[],
    descriptor: MouthDescriptor,
    fallbackDescriptor: MouthDescriptor,
  ): Scan {
    return (
      scans.find((scan) => scan.descriptor === descriptor) ??
      scans.find((scan) => scan.descriptor === fallbackDescriptor)
    );
  }

  render() {
    const subject = this.props.model.subject;

    this.useHash = false;
    return (
      <>
        {this.state.showAlbum && (
          <ModalAlbumViewer
            hide={this.hideAlbum}
            portraitPhotos={[...this.props.model.galleryPhotos].filter((scan) =>
              PortraitDescriptors.includes(scan.descriptor),
            )}
            landscapePhotos={[...this.props.model.galleryPhotos].filter((scan) =>
              LandscapeDescriptors.includes(scan.descriptor),
            )}
            action={this.props.actions.downloadGalleryPhotos}
            selectedPhoto={this.state.selectedPhoto}
          />
        )}
        {this.props.actions.shareWithPatient && this.state.showShareReport && (
          <ShareReportModal
            hide={this.hideShareReport}
            onEditSharingUpdate={this.onEditSharingUpdate}
            reportUid={this.state.report.reportUid}
            shareAction={this.props.actions.shareWithPatient}
            reportType={this.props.model.reportType}
            editSharingAction={{
              ...this.props.actions.editSharing,
              ...this.state.editSharing,
            }}
          />
        )}
        {this.props.demoMode && (
          <div className="message danger h-flex centered demo-banner">
            {i18n["Page.AssessmentResult.DemoBanner.Message"]}
          </div>
        )}
        <AssessmentResultHeader
          report={this.state.report}
          actions={this.getActions()}
          patientFileUrl={
            this.props.actions.goToPatientFile ? this.props.actions.goToPatientFile.url : null
          }
          patientPortalAccountCreationUrl={this.props.patientPortalAccountCreationUrl}
        />
        <div className="tabs">
          <div className="container">
            {this.displayedTabs.map((tab) => (
              <div
                key={tab}
                className={"tab" + (tab == this.state.activeTab ? " active" : "")}
                onClick={() => this.setState({ activeTab: tab })}
              >
                {this.tabLabels[tab]}
              </div>
            ))}
          </div>
        </div>
        <div
          className={
            `report ${this.state.activeTab}` +
            (this.props.actions.shareWithPatient || this.props.demoMode ? " editable" : "")
          }
        >
          <div className="container">{this.renderTab(this.state.activeTab)}</div>
        </div>
        {this.props.model.satisfaction && (
          <SatisfactionForm satisfaction={this.props.model.satisfaction} />
        )}
        {this.props.actions.bookingAppointment && !this.props.bookingExtendedModule && (
          <BookingRequestForm
            bookingApiUrl={this.props.actions.bookingAppointment.bookingApiUrl}
            patientHasRequestedBooking={this.props.model.patientHasRequestedBooking}
            cellPhoneNumber={subject.phoneNumber}
            countries={this.props.countries}
            doctorBookingSystemUrl={this.props.actions.bookingAppointment.doctorBookingSystemUrl}
          />
        )}
      </>
    );
  }

  private getActions() {
    const actions = [];
    if (this.props.actions.troubleshoot) {
      actions.push(
        <TextAction
          key="troubleshoot"
          onClick={() => (document.location.href = this.props.actions.troubleshoot.url)}
          icon={<i className="ic_search"/>}
          label={i18n["Page.AssessmentResult.Action.Troubleshoot"]}
          disabled={this.props.demoMode}
        />,
      );
    }
    actions.push(
      <TextAction
        key="album-modal"
        onClick={this.showAlbum}
        icon={<i className="ic_image" />}
        label={i18n["Page.AssessmentResult.Action.SeePhotos"]}
      />,
    );
    if (this.props.actions.allowPrint) {
      actions.push(
        <TextAction
          key="album-print"
          onClick={() => this.displayPrintableReport(this.model)}
          icon={<i className="ic_print" />}
          label={i18n["Page.AssessmentResult.Action.Print"]}
          additionalClass="no-mobile"
        />,
      );
    }

    if (this.props.demoMode || this.props.actions.shareWithPatient) {
      actions.push(
        <TextAction
          key="share-report-modal"
          onClick={this.props.actions.shareWithPatient && this.showShareReport}
          icon={<ShareMailIcon id="share-mail-icon" hoverable={false}/>}
          label={i18n["Page.AssessmentResult.Action.ShareReport"]}
          disabled={this.props.demoMode}
        />,
      );
    }
    return actions;
  }

  private displayPrintableReport(data: ReportModel) {
    localStorage.setItem(LOCAL_STORAGE_PRINTING_MODEL_KEY, JSON.stringify(data));

    const location = document.location.pathname;
    window.open(location + (location.endsWith("/") ? "" : "/") + "print");
  }

  private showAlbum = () => {
    // prevent double scrollbar
    document.body.style.overflow = "hidden";
    this.setState({ showAlbum: true, selectedPhoto: null });
  };

  private showAlbumWithPhoto = (photo: Photo) => {
    // prevent double scrollbar
    document.body.style.overflow = "hidden";
    this.setState({ showAlbum: true, selectedPhoto: photo });
  };
  private hideAlbum = () => {
    document.body.style.overflow = "auto";
    this.setState({ showAlbum: false });
  };
  private showShareReport = () => {
    // prevent double scrollbar
    document.body.style.overflow = "hidden";
    this.setState({ showShareReport: true });
  };
  private hideShareReport = () => {
    document.body.style.overflow = "auto";
    this.setState({ showShareReport: false });
  };
  private onEditSharingUpdate = (editSharingState: EditSharingOption) =>
    this.setState({ editSharing: editSharingState });

  private saveTreatmentSelection(treatmentSelection: TreatmentSelection) {
    const updateModel = () => {
      const index = this.model.treatmentSelections.findIndex(
        (s) => s.uid === treatmentSelection.uid,
      );
      this.model.treatmentSelections.splice(index, 1);
      this.model.treatmentSelections.splice(
        index,
        0,
        TreatmentSelection.toModel(treatmentSelection),
      );
      this.setState({ report: assembleReport(this.model) });
    };

    if (!this.props.demoMode) {
      sfetch(treatmentSelection.apiUrl, {
        method: "PUT",
        body: JSON.stringify(treatmentSelection.selectedTreatments.map((it) => it.uid)),
        headers: {
          "Content-Type": "application/json",
        },
      }).then(updateModel);
    } else {
      updateModel();
    }
  }

  private updateModelWithNote(note: string) {
    this.model.note = note;
    this.setState({ report: assembleReport(this.model) });
  }

  private toggleTreatedIssue(issue: AnalysedCriterion) {
    const modelCriterion = this.model.observations.find(
      (criterion) => issue.criterionId === criterion.criterionId,
    );
    modelCriterion.isTreated = !modelCriterion.isTreated;
    this.setState({ report: assembleReport(this.model) }, () => {
      if (this.props.actions.updateIssuesToTreat) {
        const isTreatedByCriterion = this.model.observations.reduce(
          (acc, c) => ((acc[c.uid] = c.isTreated), acc),
          {},
        );
        sfetch(this.props.actions.updateIssuesToTreat.url, {
          method: "POST",
          body: JSON.stringify(isTreatedByCriterion),
          headers: { "Content-Type": "application/json" },
        });
      }
    });
  }

  static loadComponent(elementId: string, props: AssessmentResultProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<AssessmentResult {...props} />);
  }
}
